import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { Box, Card, Stepper, Step, StepLabel, Typography, Fade, Alert, CircularProgress } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import Page from "src/components/Page";
import AboutYouStep from "../signup/AboutYouStep";
import PlanSelectionStep from "./PlanSelectionStep";
import ReviewStep from "./ReviewStep";
import CheckoutPage from "./CheckoutPage";
import { onboardingValidationSchema } from "src/util/validationSchemas";
import { onboardingService } from "src/services";
import { useDispatch, useSelector } from "react-redux";
import { onboardingReducerKey } from "src/store/onboarding/onboarding.constants";
import { setOnboardingData, clearOnboardingData } from "src/store/onboarding/onboarding.slice";

const RootStyle = styled(Page)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  backgroundColor: "#f6f7ff",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

// "Your Needs" step has been removed — About You now leads straight into
// Plan Selection.
const STEPS = ["About You", "Plan Selection", "Review & Pay", "Checkout"];
const TOTAL_STEPS = STEPS.length;

// Fields where the API's key differs from the onboarding form field name.
// Anything NOT listed here already shares a name in both places, so a
// brand-new field the backend starts returning flows straight through
// with zero changes here. Only add a line if a future field's API name
// doesn't match the form field it should fill.
const USER_API_FIELD_ALIASES = {
  name: "companyName",
  phone: "phoneNumber",
};

// Fields the API may still send that we no longer collect in onboarding
// (Accepted Terms and Use Case have been removed from the flow entirely).
const IGNORED_USER_API_FIELDS = new Set(["accepted_terms", "use_case"]);

// Converts the raw user object from getOnboardingUserData into the shape
// the form/store use. Only fields actually present (not null/undefined)
// are included — today that's firstName, lastName, email, country, name
// (-> companyName), phone (-> phoneNumber), gst, and userId. When the
// backend adds more fields, they'll populate here automatically.
function mapApiUserToOnboardingData(user = {}) {
  const mapped = {};
  Object.entries(user).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (IGNORED_USER_API_FIELDS.has(key)) return;
    const targetKey = USER_API_FIELD_ALIASES[key] ?? key;
    mapped[targetKey] = value;
  });
  return mapped;
}

export default function PlanCheckoutFlow() {
  const [searchParams] = useSearchParams();
  const urlUserId = searchParams.get("userId");
  const isVerified = searchParams.get("status") === "success";

  const dispatch = useDispatch();
  const persisted = useSelector((state) => state[onboardingReducerKey]);

  const userId = urlUserId ?? persisted?.userId;

  const [isLoadingUserData, setIsLoadingUserData] = useState(Boolean(isVerified && userId));
  const [loadUserDataError, setLoadUserDataError] = useState(null);


  useEffect(() => {
    if (!isVerified) {
      dispatch(clearOnboardingData());
    }
  }, [isVerified, dispatch]);

  useEffect(() => {
    if (!isVerified || !userId) {
      setIsLoadingUserData(false);
      return;
    }

    let isCancelled = false;

    const fetchOnboardingUserData = async () => {
      setIsLoadingUserData(true);
      setLoadUserDataError(null);
      try {
        const response = await onboardingService.getOnboardingUserData(userId);
        const user = response?.data?.user;


        if (!isCancelled) {
          if (response?.data?.success && user) {
            const mappedFields = mapApiUserToOnboardingData(user);
           
            dispatch(setOnboardingData({ ...persisted, ...mappedFields }));
          } else {
            setLoadUserDataError("Couldn't find your onboarding details. Please try again.");
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setLoadUserDataError(
            err?.response?.data?.message || "Couldn't load your details. Please try again."
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingUserData(false);
        }
      }
    };

    fetchOnboardingUserData();

    return () => {
      isCancelled = true;
    };
    
  }, [isVerified, userId]);

  // Plan Selection is now step index 1 (was 2 before Your Needs was removed).
  const [activeStep, setActiveStep] = useState(1);
  const [plan, setPlan] = useState(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [checkoutInitData, setCheckoutInitData] = useState(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: persisted?.firstName ?? "",
      lastName: persisted?.lastName ?? "",
      email: persisted?.email ?? "",
      phoneNumber: persisted?.phoneNumber ?? "",
      gst: persisted?.gst ?? "",
      country: persisted?.country ?? "",
      companyName: persisted?.companyName ?? "",
    },
    validationSchema: onboardingValidationSchema,
    onSubmit: () => {},
  });

  const account = {
    name: `${formik.values.firstName} ${formik.values.lastName}`.trim(),
    email: formik.values.email,
    company: formik.values.companyName,
  };

  const validateStepFields = async (fields) => {
    try {
      await onboardingValidationSchema.pick(fields).validate(formik.values, {
        abortEarly: false,
      });
      return true;
    } catch (err) {
      const errors = {};
      const touchedFields = {};
      (err.inner ?? []).forEach((e) => {
        errors[e.path] = e.message;
        touchedFields[e.path] = true;
      });
      formik.setErrors({ ...formik.errors, ...errors });
      formik.setTouched({ ...formik.touched, ...touchedFields });
      return false;
    }
  };

  // About You now leads straight into Plan Selection (Your Needs is gone).
  // When readOnly (post-verification), the fields are just a recap of what
  // was already submitted at signup — this only advances the step and
  // never creates an account.
  const handleAboutYouNext = async () => {
    const fields = [
      "firstName",
      "lastName",
      "email",
      "country",
      "phoneNumber",
      "companyName",
    ];

    if (await validateStepFields(fields)) {
      setActiveStep(1);
    }
  };

  const handlePlanNext = (payload) => {
    setPlan(payload);
    setActiveStep(2);
  };

const handleReviewConfirm = async (costBreakdown) => {
    if (!plan) return;

    setIsSubmittingReview(true);
    setReviewError(null);

    try {
      const payload = {
        userId,
        amount: costBreakdown.total_cost,
        ac_count: costBreakdown.ac_count,
        dc_count: costBreakdown.dc_count,
        ac_charges: costBreakdown.ac_charges,
        dc_charges: costBreakdown.dc_charges,
        subtotal: costBreakdown.subtotal,
        gateway_fee: costBreakdown.gateway_fee,
        tax_amount: costBreakdown.tax_amount,
        total_cost: costBreakdown.total_cost,
        duration: plan.duration,
        currency: plan.currency,
      };

      const response = await onboardingService.initiatePaymentOnboarding(payload);
      setCheckoutInitData(response);
      setActiveStep(3);
    } catch (err) {
      setReviewError(err?.response?.data?.message || "Couldn't start payment. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  if (activeStep === 3 && plan) {
    return (
      <Page title="Checkout | Smart-CMS">
        <CheckoutPage
          plan={plan}
          account={account}
          onboarding={checkoutInitData?.data}
          onBack={() => setActiveStep(2)}
        />
      </Page>
    );
  }

  if (isLoadingUserData) {
    return (
      <RootStyle title="Plan Selection | Smart-CMS">
        <Box sx={{ width: "100%", minHeight: "100vh", display: "grid", placeItems: "center" }}>
          <CircularProgress />
        </Box>
      </RootStyle>
    );
  }

  const cardMaxWidth = activeStep === 1 ? "100%" : 720;

  return (
    <RootStyle title="Plan Selection | Smart-CMS">
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: { xs: "40px 16px", md: "60px 32px" },
        }}
      >
        <Card sx={{ width: "100%", maxWidth: cardMaxWidth, p: { xs: 3, md: 4 }, borderRadius: "15px" }}>
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Typography variant="caption" color="text.secondary" align="center" sx={{ display: "block", mt: 1 }}>
              Step {activeStep + 1} of {TOTAL_STEPS}
            </Typography>
          </Box>

          {loadUserDataError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadUserDataError(null)}>
              {loadUserDataError}
            </Alert>
          )}

          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReviewError(null)}>
              {reviewError}
            </Alert>
          )}

          <Fade in timeout={300} key={activeStep}>
            <Box>
              {activeStep === 0 && (
                <FormikProvider value={formik}>
                  <AboutYouStep
                    onSubmit={handleAboutYouNext}
                    readOnly={isVerified}
                    submitLabel="Next"
                  />
                </FormikProvider>
              )}
              {activeStep === 1 && (
                <PlanSelectionStep onNext={handlePlanNext} onBack={() => setActiveStep(0)} country={persisted?.country}  />
              )}
              {activeStep === 2 && plan && (
                <ReviewStep
                  account={account}
                  plan={plan}
                  isSubmitting={isSubmittingReview}
                  onBack={() => setActiveStep(1)}
                  onConfirm={handleReviewConfirm}
                />
              )}
            </Box>
          </Fade>
        </Card>
      </Box>
    </RootStyle>
  );
}